/**
 * Turning `get_msgs` rows into `NotificationApiItem` (MYP-112).
 *
 * The API document (p.29) claims rows shaped `{id, type, content, ...}`. Live rows carry none of
 * that — see `NotificationApiItem`'s doc comment for the field-by-field confirmation. Nothing
 * here is inferred from the document, which is simply wrong about this endpoint's shape.
 *
 * `msgdate` goes through the same `toIsoTimestamp` conversion `package_list`/`order_list` already
 * use, rather than a second copy of that reasoning — it is the same zoneless MySQL datetime, in
 * the same warehouse offset.
 *
 * The endpoint does not sort by date: a full fetch comes back grouped by `type` first (every
 * PACKAGE row, then every ORDER row, and so on — confirmed live, 103 rows, PACKAGE/ORDER/AB/
 * ALIPAY/AUTH/COMPEN in that order), each group internally newest-first rather than the whole
 * response being one newest-first feed. Whatever calls this is responsible for sorting the result
 * by `msgdate` itself before showing it as one feed — see `NotificationsProvider`.
 */

import type { NotificationApiItem } from './types.js';
import { toIsoTimestamp } from './packages-wire.js';

type NotificationRow = Readonly<Record<string, unknown>>;

function text(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/**
 * One row → one `NotificationApiItem`, or `undefined` for a row missing a field every type is
 * confirmed to carry (`tid`, `type`, `msgdate`, `readflag`). `recvuserid`/`type_cn`/`type_en`/
 * `orderno` are all optional on the type itself, so a row missing one of those is still usable —
 * only `orderno`'s absence is actually expected, on ALIPAY/AUTH/COMPEN rows.
 */
export function normaliseNotification(row: unknown): NotificationApiItem | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const record = row as NotificationRow;

  const type = text(record['type']);
  const tid = text(record['tid']);
  const msgdate = toIsoTimestamp(record['msgdate']);
  const readflag = text(record['readflag']);
  if (type === undefined || tid === undefined || msgdate === undefined || readflag === undefined) {
    return undefined;
  }

  return {
    type,
    tid,
    msgdate,
    readflag,
    recvuserid: text(record['recvuserid']),
    type_cn: text(record['type_cn']),
    type_en: text(record['type_en']),
    orderno: text(record['orderno']),
  };
}

/** Every usable row, in whatever order the vendor sent them — see the file doc comment. */
export function normaliseNotifications(rows: readonly unknown[]): readonly NotificationApiItem[] {
  const items: NotificationApiItem[] = [];

  for (const row of rows) {
    const parsed = normaliseNotification(row);
    if (parsed) items.push(parsed);
  }

  return items;
}
